<template>
  <div class="chart-box">
    <div v-for="r in rows" :key="r.label" class="fee-row">
      <div class="f-label">{{ r.label }}<span class="f-sub">{{ r.sub }}</span></div>
      <div class="f-track">
        <div v-if="r.range" class="f-range" :style="{ left: pct(r.range[0]), width: pct(r.range[1] - r.range[0]) }"></div>
        <div v-else class="f-bar" :class="{ 'f-bar-pvis': r.pvis }" :style="{ width: pct(r.value) }"></div>
      </div>
      <div class="f-val">
        <template v-if="r.range">AUD {{ (r.range[0] / 1000).toFixed(0) }}k–{{ (r.range[1] / 1000).toFixed(0) }}k</template>
        <template v-else>AUD {{ (r.value / 1000).toFixed(1) }}k</template>
      </div>
    </div>
    <div class="fee-row axis-row">
      <div></div>
      <div class="axis">
        <span v-for="t in ticks" :key="t" class="axis-tick" :style="{ left: pct(t) }">
          <i></i>AUD {{ t / 1000 }}k
        </span>
      </div>
      <div></div>
    </div>
    <p class="note">
      Annual tuition converted to AUD ({{ AUD_PER_VUV }} AUD/VUV; USD figures at ~1.50 AUD/USD).
      International School Suva figures are 2023 published expat rates; the Australian band is an indicative
      independent day-school range. Figures are indicative for positioning, not quotes.
    </p>
  </div>
</template>

<script>
import { CLASSROOM_TYPES, AUD_PER_VUV } from '../../utils/boardEngine';

const NON_CITIZEN_Y78_FEE_VUV = 845041.05; // Tuition Fees tab, 2027 (flat from 2026)
const AUD_PER_USD = 1.5;

export default {
  name: 'FeeBenchmark',
  props: { feeIncreasePct: { type: Number, default: 0 } },
  data: () => ({ AUD_PER_VUV }),
  computed: {
    feeMult() { return 1 + this.feeIncreasePct / 100; },
    rows() {
      const localY78 = CLASSROOM_TYPES.y78.fee2027 * this.feeMult * AUD_PER_VUV;
      const nonCitY78 = NON_CITIZEN_Y78_FEE_VUV * this.feeMult * AUD_PER_VUV;
      return [
        { label: 'PVIS · Years 7–8, local', sub: 'at the current fee lever', value: localY78, pvis: true },
        { label: 'PVIS · Years 7–8, non-citizen', sub: 'at the current fee lever', value: nonCitY78, pvis: true },
        { label: 'International School Suva · Years 6–8', sub: 'expat rate, 2023', value: 15092 * AUD_PER_USD },
        { label: 'International School Suva · Years 9–10', sub: 'expat rate, 2023', value: 17841 * AUD_PER_USD },
        { label: 'Australian independent day schools', sub: 'indicative metro range', range: [15000, 35000] },
      ];
    },
    max() {
      return Math.max(...this.rows.map((r) => (r.range ? r.range[1] : r.value))) * 1.08;
    },
    ticks() {
      const out = [];
      for (let t = 0; t <= this.max; t += 10000) out.push(t);
      return out;
    },
  },
  methods: {
    pct(v) { return `${(v / this.max) * 100}%`; },
  },
};
</script>

<style scoped>
.fee-row {
  display: grid; grid-template-columns: minmax(180px, 1.3fr) 2.2fr minmax(90px, 0.7fr);
  gap: 12px; align-items: center; margin-bottom: 10px;
}
.f-label { font-size: 12px; font-weight: 600; color: var(--ink); }
.f-sub { display: block; font-weight: 400; font-size: 10px; color: var(--muted); }
.f-track { position: relative; background: var(--track); height: 10px; }
.f-bar { height: 100%; background: var(--muted); }
.f-bar-pvis { background: var(--red); }
.f-range { position: absolute; top: 0; height: 100%; background: var(--muted); opacity: 0.55; }
.f-val { font-size: 12px; text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; }
.axis-row { margin-top: -2px; margin-bottom: 4px; }
.axis { position: relative; height: 22px; border-top: 1px solid var(--hairline); }
.axis-tick {
  position: absolute; top: 0; transform: translateX(-50%);
  font-size: 9.5px; color: var(--muted); font-variant-numeric: tabular-nums; white-space: nowrap;
}
.axis-tick:first-child { transform: none; }
.axis-tick i { display: block; width: 1px; height: 4px; background: var(--muted); margin: 0 auto 2px; }
.axis-tick:first-child i { margin: 0 0 2px; }
.note { font-size: 10.5px; color: var(--muted); line-height: 1.55; margin: 10px 0 0; }
</style>
